import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Sun, Moon, FilmStrip, DoorOpen, Lightning, Check, Play
} from '@phosphor-icons/react';

const ICON_MAP = {
  Sun, Moon, FilmStrip, DoorOpen, Lightning,
};

export function SceneShortcuts({ compact = false }) {
  const { api } = useAuth();
  const [scenes, setScenes] = useState([]);
  const [triggering, setTriggering] = useState('');
  const [triggered, setTriggered] = useState('');

  const fetchScenes = useCallback(async () => {
    try {
      const res = await api.get('/scenes');
      setScenes(res.data);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { fetchScenes(); }, [fetchScenes]);

  const handleTrigger = async (sceneId) => {
    setTriggering(sceneId);
    try {
      await api.post(`/scenes/${sceneId}/trigger`);
      setTriggered(sceneId);
      setTimeout(() => setTriggered(''), 2000);
    } catch (e) { console.error(e); }
    setTriggering('');
  };

  if (!scenes.length) return null;

  if (compact) {
    return (
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="scene-shortcuts-compact">
        {scenes.map(scene => {
          const Icon = ICON_MAP[scene.icon] || Lightning;
          const isDone = triggered === scene.scene_id;
          const isLoading = triggering === scene.scene_id;
          return (
            <button
              key={scene.scene_id}
              data-testid={`scene-${scene.scene_id}`}
              onClick={() => handleTrigger(scene.scene_id)}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2.5 rounded-2xl whitespace-nowrap transition-all active:scale-95 flex-shrink-0"
              style={{
                background: isDone ? `${scene.color}15` : 'rgba(255,255,255,0.03)',
                border: `1px solid ${isDone ? scene.color + '30' : 'rgba(255,255,255,0.06)'}`,
              }}
            >
              {isDone ? <Check size={16} weight="bold" style={{ color: scene.color }} /> :
                isLoading ? <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: scene.color, borderTopColor: 'transparent' }} /> :
                <Icon size={16} weight="fill" style={{ color: scene.color }} />}
              <span className="text-xs font-semibold" style={{ color: isDone ? scene.color : '#A3A3A3' }}>
                {scene.name}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div data-testid="scene-shortcuts">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white">Scene Shortcuts</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {scenes.map(scene => {
          const Icon = ICON_MAP[scene.icon] || Lightning;
          const isDone = triggered === scene.scene_id;
          const isLoading = triggering === scene.scene_id;
          return (
            <button
              key={scene.scene_id}
              data-testid={`scene-${scene.scene_id}`}
              onClick={() => handleTrigger(scene.scene_id)}
              disabled={isLoading}
              className="flex flex-col items-center gap-2.5 p-4 rounded-2xl transition-all active:scale-95"
              style={{
                background: isDone ? `${scene.color}12` : 'rgba(255,255,255,0.02)',
                border: `1px solid ${isDone ? scene.color + '30' : 'rgba(255,255,255,0.06)'}`,
              }}
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: scene.color + '12' }}>
                {isDone ? <Check size={24} weight="bold" style={{ color: scene.color }} /> :
                  isLoading ? <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: scene.color, borderTopColor: 'transparent' }} /> :
                  <Icon size={24} weight="fill" style={{ color: scene.color }} />}
              </div>
              <div className="text-center">
                <p className="text-xs font-semibold text-white">{scene.name}</p>
                {scene.name_hi && <p className="text-[10px] text-neutral-500 mt-0.5">{scene.name_hi}</p>}
              </div>
              {isDone && <span className="text-[10px] font-bold uppercase" style={{ color: scene.color }}>Activated</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
